import { useState } from "react";
import LogoEditor from "./LogoEditor";
import type { UserRole } from "@/utils/userStore";
import { siteData, GeneralSettings, defaultGeneralSettings } from "@/store/siteData";

const Field = ({ label, value, onChange, type = "text", placeholder = "" }: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  type?: string;
  placeholder?: string;
}) => (
  <div>
    <label className="block text-[#7B8FAF] text-xs mb-2 uppercase tracking-widest">{label}</label>
    {type === "textarea" ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-4 py-3 outline-none focus:border-[#3B82F6] transition-colors resize-none placeholder-[#5E7398]"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-4 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]"
      />
    )}
  </div>
);

interface SiteSettingsProps {
  userRole?: UserRole;
}

type SectionId = "general" | "hero" | "social" | "logo";

export default function SiteSettings(_props: SiteSettingsProps) {
  const [settings, setSettings] = useState<GeneralSettings>(() => siteData.getGeneralSettings());
  const [heroSettings, setHeroSettings] = useState(() => siteData.getHero());
  const [ctaSettings, setCtaSettings] = useState(() => siteData.getCTA());

  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId>("general");

  const update = (key: keyof GeneralSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    await siteData.saveGeneralSettings(settings);
    await siteData.saveHero(heroSettings);
    await siteData.saveCTA(ctaSettings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = async () => {
    setSettings(defaultGeneralSettings);
    await siteData.saveGeneralSettings(defaultGeneralSettings);
    setSaved(false);
  };

  const sections: { id: SectionId; label: string; icon: string }[] = [
    { id: "general", label: "Şirket Bilgileri",    icon: "ri-building-line" },
    { id: "hero",    label: "Ana Sayfa Metinleri", icon: "ri-layout-top-2-line" },
    { id: "logo",    label: "Logo Yönetimi",       icon: "ri-image-line" },
    { id: "social",  label: "Sosyal Medya",        icon: "ri-share-circle-line" },
  ];

  const showSaveBar = activeSection !== "logo";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Site Ayarları</h1>
          <p className="text-[#7B8FAF] text-sm mt-0.5">Şirket bilgileri ve içerik yönetimi</p>
        </div>
        {showSaveBar && (
          <div className="flex items-center gap-3">
            <button onClick={handleReset}
              className="px-4 py-2 text-sm text-[#7B8FAF] border border-[#2B3654] rounded-lg hover:border-red-500/40 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap">
              Sıfırla
            </button>
            <button onClick={handleSave}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#2563EB] text-white"}`}>
              <div className="w-4 h-4 flex items-center justify-center"><i className={saved ? "ri-check-line" : "ri-save-line"}></i></div>
              {saved ? "Kaydedildi!" : "Kaydet"}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-1">
          {sections.map((sec) => (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap text-left ${
                activeSection === sec.id ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40" : "text-[#7B8FAF] hover:text-white hover:bg-white/5"
              }`}>
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${sec.icon} text-base`}></i></div>
              {sec.label}
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 bg-[#141E35] border border-[#2B3654] rounded-xl p-6 space-y-5">
          {activeSection === "general" && (
            <>
              <h2 className="text-white font-semibold text-base pb-4 border-b border-[#2B3654]">Şirket Bilgileri</h2>
              <Field label="Şirket Adı" value={settings.companyName} onChange={v => update("companyName", v)} />
              <Field label="Slogan / Tagline" value={settings.tagline} onChange={v => update("tagline", v)} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Telefon" value={settings.phone} onChange={v => update("phone", v)} type="tel" />
                <Field label="E-posta" value={settings.email} onChange={v => update("email", v)} type="email" />
              </div>
              <Field label="Adres" value={settings.address} onChange={v => update("address", v)} type="textarea" />
              <Field label="Kuruluş Yılı" value={settings.foundedYear} onChange={v => update("foundedYear", v)} />
            </>
          )}

          {activeSection === "hero" && (
            <>
              <h2 className="text-white font-semibold text-base pb-4 border-b border-[#2B3654]">Ana Sayfa Metinleri</h2>
              <Field label="Hero Başlık" value={heroSettings.title} onChange={v => { setHeroSettings(p => ({ ...p, title: v })); setSaved(false); }} />
              <Field label="Hero Alt Metin" value={heroSettings.subtitle} onChange={v => { setHeroSettings(p => ({ ...p, subtitle: v })); setSaved(false); }} type="textarea" />
              <div className="h-px bg-[#2B3654] my-2"></div>
              <Field label="CTA Başlık" value={ctaSettings.title} onChange={v => { setCtaSettings(p => ({ ...p, title: v })); setSaved(false); }} />
              <Field label="CTA Alt Metin" value={ctaSettings.subtitle} onChange={v => { setCtaSettings(p => ({ ...p, subtitle: v })); setSaved(false); }} type="textarea" />
            </>
          )}

          {activeSection === "logo" && <LogoEditor />}

          {activeSection === "social" && (
            <>
              <h2 className="text-white font-semibold text-base pb-4 border-b border-[#2B3654]">Sosyal Medya Hesapları</h2>
              <div className="space-y-4">
                {[
                  { label: "Instagram Kullanıcı Adı", fieldKey: "socialInstagram" as keyof GeneralSettings, icon: "ri-instagram-line", prefix: "@" },
                  { label: "LinkedIn Kullanıcı Adı", fieldKey: "socialLinkedin" as keyof GeneralSettings, icon: "ri-linkedin-box-line", prefix: "linkedin.com/company/" },
                  { label: "YouTube Kanal Adı", fieldKey: "socialYoutube" as keyof GeneralSettings, icon: "ri-youtube-line", prefix: "youtube.com/@" },
                ].map((item) => (
                  <div key={item.fieldKey}>
                    <label className="block text-[#7B8FAF] text-xs mb-2 uppercase tracking-widest">{item.label}</label>
                    <div className="flex items-center bg-[#0F1729] border border-[#2B3654] rounded-lg overflow-hidden focus-within:border-[#3B82F6] transition-colors">
                      <div className="w-10 h-10 flex items-center justify-center border-r border-[#2B3654] bg-[#141E35] flex-shrink-0"><i className={`${item.icon} text-[#3B82F6]`}></i></div>
                      <span className="text-[#5E7398] text-sm px-2 whitespace-nowrap hidden sm:block">{item.prefix}</span>
                      <input type="text" value={settings[item.fieldKey]} onChange={(e) => update(item.fieldKey, e.target.value)} className="bg-transparent text-white text-sm flex-1 px-3 py-3 outline-none" />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {showSaveBar && (
            <div className="pt-4 border-t border-[#2B3654] flex justify-end">
              <button onClick={handleSave}
                className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#2563EB] text-white"}`}>
                <div className="w-4 h-4 flex items-center justify-center"><i className={saved ? "ri-check-line" : "ri-save-line"}></i></div>
                {saved ? "Kaydedildi!" : "Değişiklikleri Kaydet"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
